import React, { useEffect, useState, useCallback } from "react";
import { BASEURL, ERRORMESSAGES, STORAGE_KEYS } from "../../utils/constants";
import BEAGLELOGO from "../../static/beagle-logo.svg";
import { setUserDetails } from "../../utils/loginSlice";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import useOnline from "../../utils/useOnline";
import { setShowChat } from "../../utils/chatSlice";
import { useIsMobile } from "../../hooks/useMediaQuery";
import { Eye, EyeOff, AlertCircle } from "lucide-react";

const LoginComponent = () => {
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginFailureMessage, setLoginFailureMessage] = useState("");
  const [needsInteraction, setNeedsInteraction] = useState(false);
  const [loading, setLoading] = useState(false);

  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isOnline = useOnline();
  const isMobile = useIsMobile();

  const refreshIdToken = useCallback(async (options = { interactive: false, force: false }) => {
    try {
      const activeAccount = instance?.getActiveAccount?.() || (accounts && accounts[0]);
      if (!activeAccount) return;

      let response;
      try {
        response = await instance.acquireTokenSilent({
          scopes: ["openid", "profile", "email"],
          account: activeAccount,
          forceRefresh: options.force || false,
        });
      } catch (silentErr) {
        if (!options.force) {
          response = await instance.acquireTokenSilent({
            scopes: ["openid", "profile", "email"],
            account: activeAccount,
            forceRefresh: true,
          });
        } else {
          throw silentErr;
        }
      }

      const tokenToUse = response?.idToken || response?.accessToken;
      if (tokenToUse) {
        await loginMsUser(activeAccount.username, tokenToUse);
        setNeedsInteraction(false);
      }
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        setNeedsInteraction(true);
        if (options.interactive) {
          try {
            const popupResp = await instance.acquireTokenPopup({
              scopes: ["openid", "profile", "email"],
            });
            const tokenToUse = popupResp?.idToken || popupResp?.accessToken;
            if (tokenToUse) {
              await loginMsUser(popupResp.account?.username || instance.getActiveAccount()?.username, tokenToUse);
              setNeedsInteraction(false);
            }
          } catch (popupErr) {
            console.error("Popup token acquire failed:", popupErr);
          }
        }
      } else {
        console.error("Token refresh error:", error);
      }
    }
  }, [instance, accounts]);

  useEffect(() => {
    const userDetails = JSON.parse(localStorage.getItem(STORAGE_KEYS.SESSION)) || {};
    if (userDetails.token && userDetails.conversationId) {
      navigate("/beagle-chat", { replace: true });
    }
  }, [navigate]);

  const loginMsUser = async (userName, accessToken) => {
    let data = { email: userName };
    setSubmitted(true);
    setLoading(true);

    try {
      const response = await fetch(`${BASEURL}/api/auth/login`, {
        method: "POST",
        body: JSON.stringify(data),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const result = await response.json();
      if (result?.error) {
        setLoginFailureMessage(result.error);
        setLoading(false);
        return;
      }

      localStorage.setItem(STORAGE_KEYS.SESSION, JSON.stringify(result));
      dispatch(setUserDetails(result));

      if (userName.trim() !== "" && result.conversationId && result.email) {
        if (isMobile) dispatch(setShowChat(true));
        navigate("/beagle-chat");
      }
    } catch (err) {
      console.error("Login failed:", err);
      setLoginFailureMessage("Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const loginUser = async () => {
    if (isOnline === false) {
      setLoginFailureMessage(ERRORMESSAGES.INTERNETLOSS);
      return;
    }

    setSubmitted(true);
    if (!userName.trim() || !password.trim()) return;

    setLoading(true);
    try {
      const response = await fetch(`${BASEURL}/api/auth/login`, {
        method: "POST",
        body: JSON.stringify({ email: userName, password }),
        headers: { "Content-Type": "application/json" },
      });

      const result = await response.json();
      if (result?.error) {
        setLoginFailureMessage(result.error);
        setLoading(false);
        return;
      }

      localStorage.setItem(STORAGE_KEYS.SESSION, JSON.stringify(result));
      dispatch(setUserDetails(result));

      if (userName.trim() !== "" && result.conversationId && result.email) {
        if (isMobile) dispatch(setShowChat(true));
        navigate("/beagle-chat");
      }
    } catch (err) {
      console.error("Login failed:", err);
      setLoginFailureMessage("Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    try {
      setLoading(true);
      const loginResponse = await instance.loginPopup({
        scopes: ["openid", "profile", "email"],
      });

      instance.setActiveAccount(loginResponse.account);

      try {
        await refreshIdToken({ interactive: true, force: false });
      } catch (e) {
        console.error("Error refreshing token after login:", e);
      }

      await loginMsUser(loginResponse.account.username, loginResponse.idToken);
    } catch (error) {
      console.error("Login error:", error);
      sessionStorage.clear();
    } finally {
      setLoading(false);
    }
  };

  const handleReauthenticate = async () => {
    try {
      await refreshIdToken({ interactive: true, force: true });
    } catch (e) {
      console.error("Re-authenticate failed:", e);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") loginUser();
  };

  useEffect(() => {
    const explicitLogout = sessionStorage.getItem(STORAGE_KEYS.LOGOUT_FLAG);
    if (explicitLogout) {
      const timer = setTimeout(() => {
        sessionStorage.removeItem(STORAGE_KEYS.LOGOUT_FLAG);
      }, 2000);
      return () => clearTimeout(timer);
    }

    try {
      refreshIdToken({ interactive: false, force: false });
    } catch (e) {
      console.error("Error triggering immediate token refresh:", e);
    }

    const interval = setInterval(() => refreshIdToken({ interactive: false, force: false }), 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [refreshIdToken, accounts]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-brand-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 p-4">
      <div className="w-full max-w-md">
        {/* Logo & Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img src={BEAGLELOGO} alt="BeagleGPT" className="w-16 h-16" />
          </div>
          <h1 className="text-2xl font-bold text-[var(--text-primary)]">
            Welcome to BeagleGPT
          </h1>
          <p className="text-[var(--text-secondary)] mt-1">
            GenAI powered knowledge assistant
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-[var(--card-bg)] rounded-2xl shadow-xl border border-[var(--border-color)] p-8">
          {/* Error message */}
          {loginFailureMessage && (
            <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
              <AlertCircle size={16} className="shrink-0" />
              {loginFailureMessage}
            </div>
          )}

          {/* Email Input */}
          <div className="mb-4">
            <label htmlFor="login-email" className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">
              Email Address
            </label>
            <input
              id="login-email"
              type="email"
              name="username"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter your email"
              maxLength={100}
              autoComplete="email"
              className={`w-full px-4 py-2.5 rounded-xl border bg-[var(--input-bg)] text-[var(--text-primary)] placeholder-[var(--text-tertiary)] outline-none transition-all
                ${submitted && !userName.trim() ? 'border-red-400' : 'border-[var(--border-color)]'}
                focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20`}
            />
          </div>

          {/* Password Input */}
          <div className="mb-6">
            <label htmlFor="login-password" className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">
              Password
            </label>
            <div className="relative">
              <input
                id="login-password"
                type={showPassword ? "text" : "password"}
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter your password"
                maxLength={100}
                autoComplete="current-password"
                className={`w-full px-4 py-2.5 pr-10 rounded-xl border bg-[var(--input-bg)] text-[var(--text-primary)] placeholder-[var(--text-tertiary)] outline-none transition-all
                  ${submitted && !password.trim() ? 'border-red-400' : 'border-[var(--border-color)]'}
                  focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)] hover:text-[var(--text-secondary)] transition-colors"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Sign In Button */}
          <button
            onClick={loginUser}
            disabled={loading}
            className="w-full py-2.5 rounded-xl bg-brand-500 hover:bg-brand-600 text-white font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-[var(--border-color)]" />
            <span className="text-xs text-[var(--text-tertiary)] uppercase tracking-wider">or continue with</span>
            <div className="flex-1 h-px bg-[var(--border-color)]" />
          </div>

          {/* Microsoft SSO */}
          <button
            onClick={handleLogin}
            disabled={loading}
            className="w-full py-2.5 rounded-xl border border-[var(--border-color)] bg-[var(--input-bg)] hover:bg-[var(--bg-secondary)] text-[var(--text-primary)] font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <svg width="20" height="20" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
              <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
              <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
              <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
              <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
            </svg>
            Sign in with Microsoft
          </button>

          {/* Re-authenticate button (conditional) */}
          {needsInteraction && (
            <button
              onClick={handleReauthenticate}
              className="w-full mt-3 py-2.5 rounded-xl border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 text-sm font-medium hover:bg-amber-100 dark:hover:bg-amber-950/50 transition-colors"
            >
              Session expired — Re-authenticate
            </button>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-[var(--text-tertiary)] mt-6">
          Powered by DecisionPoint
        </p>
      </div>
    </div>
  );
};

export default LoginComponent;
